import { ACER_MAP } from '../Assets/acer.const';

export interface ResultValue {
	value: any;
	data?: string;
	settings?: {
		unit?: string | null;
	};
}

export interface QuestionnaireScoringRow {
	id: string;
	questionId: string;
	questionText: string;
	answerId: string;
	answerSubtype?: string | null;
	key: string;
	values: ResultValue[];
	maxScore: number | null;
	score?: number | null;
}

export interface FormValues {
	scores: Record<string, string>;
}

export const parseJson = (value?: string | null) => {
	if (!value) return null;

	try {
		return JSON.parse(value);
	} catch {
		return null;
	}
};

export const shouldSkipResultItem = (item: any): boolean => {
	const parsedValue = parseJson(item.value);

	if (item.answer?.subtype === 'action_buttons') return true;
	if (parsedValue?.completed === true) return true;
	if (parsedValue?.action === 'continue') return true;
	if (parsedValue?.action === 'finish_questionnaire') return true;

	return false;
};

export const getResultValue = (item: any): ResultValue[] => {
	if (item.valueJson?.type === 'file' && item.valueJson?.fileId) {
		return [
			{
				value: {
					type: 'file',
					fileId: item.valueJson.fileId,
					transcription: item.valueJson.transcription || null,
				},
			},
		];
	}

	return [
		{
			value: item.valueJson?.rawValue || item.value || item.valueJson || null,
		},
	];
};

const getAcerOrderMap = (): Record<string, number> =>
	Object.fromEntries(Object.keys(ACER_MAP).map((id, index) => [id, index]));

export const getQuestionnaireScoringRows = (
	resultItems: any[],
	questionnaireCode?: string | null,
): QuestionnaireScoringRow[] => {
	const isAcer = questionnaireCode === 'testSocialniKogniceAcer';
	const acerOrderMap = isAcer ? getAcerOrderMap() : {};

	return resultItems
		.filter(resultItem => !shouldSkipResultItem(resultItem))
		.map(resultItem => {
			const questionId = resultItem.questionId || '';
			const customConfig = isAcer ? ACER_MAP[questionId] : undefined;

			return {
				id: resultItem.id,
				questionId,
				answerId: resultItem.answerId,
				answerSubtype: resultItem.answer?.subtype,
				key: resultItem.answerId,
				questionText: customConfig?.title?.trim()
					? customConfig.title
					: questionId,
				values: getResultValue(resultItem),
				maxScore: customConfig?.maxScore ?? null,
				score: resultItem.score ?? null,
			};
		})
		.sort((a, b) => {
			if (!isAcer) return 0;

			const aIndex = acerOrderMap[a.questionId] ?? Number.MAX_SAFE_INTEGER;
			const bIndex = acerOrderMap[b.questionId] ?? Number.MAX_SAFE_INTEGER;

			return aIndex - bIndex;
		});
};

export const getScoreDefaultValues = (
	rows: QuestionnaireScoringRow[],
): FormValues => ({
	scores: rows.reduce<Record<string, string>>((acc, row) => {
		acc[row.id] =
			row.score !== null && row.score !== undefined && row.score !== 0
				? String(row.score)
				: '';

		return acc;
	}, {}),
});

export const getScoredItemsInput = (
	rows: QuestionnaireScoringRow[],
	values: FormValues,
) =>
	rows
		.filter(
			row =>
				row.maxScore !== null &&
				values.scores?.[row.id] !== '' &&
				values.scores?.[row.id] !== undefined,
		)
		.map(row => ({
			questionnaireResultItemId: row.id,
			score: Number(values.scores[row.id]),
		}));
